import {
  AudioCreatedPayload,
  AudioUpdatedPayload,
  AudioUploadProgressPayload,
  CsvImportProgressPayload,
  DownloadableResourcePayload,
  LyricsCowriterPayload,
  ReleaseExportStatusUpdatePayload,
  ReleaseIsReadyToExportEventPayload,
  ReleaseVersionAudioUpdatePayload,
  StorageChangedPayload,
  WsJsonMessage,
} from 'types/websocket.types';
import { AlertIcon } from 'app/store/Alerts/slice.types';
import {
  ChatMessageItem,
  ChatMessageUnreadCountChangedPayload,
  EditChatMessageItem,
  FetchChannelEffectData,
} from 'types/chat.types';
import { AppDispatch, AppState } from 'app/store';
import { routes } from 'app/routing/routesConfig';
import { dateToTime, isoToTime, triggerDownload } from 'app/utils/utils';
import { SNACKBAR_DEFAULT_CLOSE_TIME } from 'app/components/Alerts/constants';
import {
  handleReplaceAudioUpdate,
  updateLatestExportAction,
} from 'app/store/Releases/slice';
import { addAlertAction } from 'app/store/Alerts/slice';
import {
  updateSongAudioAction,
  updateSongAudioStatus,
} from 'app/store/SongList/slice';
import { updateStorageData } from 'app/store/Storage/slice';
import { removeFromDownloadList } from 'app/store/Shares/slice';
import { fetchSongAudioEffect } from 'app/store/SongList/effects';
import {
  fetchChatChannelsEffect,
  fetchChatUnreadCountEffect,
  fetchSongChannelEffect,
} from 'app/store/Chat/effects';
import { fetchReleaseEffect } from 'app/store/Releases/effects';
import {
  getIsAudioUploadSupported,
  getReleaseGuid,
  getReleaseSongOrVersionCount,
} from 'app/store/Releases/selectors';
import { getUserGuid } from 'app/store/Auth/selectors';
import {
  getIsFetchingAudio,
  getSongAudioByGuid,
  getSongGuidSelector,
} from 'app/store/SongList/selectors';
import { getSnackbarErrorText } from 'app/components/Song/Audio/AudioList/utils';
import { getDownloadList } from 'app/store/Shares/selectors';
import { AudioStatusType } from 'types/audio';
import {
  messageEditedAction,
  messageReceivedAction,
  updateHasUnreadMessages,
} from 'app/store/Chat/slice';
import {
  getChatChannels,
  getIsChannelsListOpen,
} from 'app/store/Chat/selectors';
import { UnreadNotificationCountChangedPayload } from 'types/notification.types';
import { updateNewNotificationsCountsAction } from 'app/store/Notifications/slice';
import {
  setAudioUploadedAction,
  updateImportProgressAction,
} from 'app/store/Import/slice';
import { BaseRelease } from 'types/releases';
import { setLyricsCowriterRequestRateLimit } from 'app/store/Subscription/slice';
import { handleReleaseExportsByType } from './handleReleaseExportsByType';
import { isPageView } from './utils';

export const handleResourceUpdate = (
  jsonMessage: WsJsonMessage,
  dispatch: AppDispatch,
  getState: () => AppState
) => {
  switch (jsonMessage.payload.payloadType) {
    case 'ReleaseExportStatusUpdate': {
      const wsPayload = jsonMessage.payload as ReleaseExportStatusUpdatePayload;
      const { releaseType } = wsPayload;
      const releaseGuid = getReleaseGuid(getState());
      const isAudioUploadSupported = getIsAudioUploadSupported(getState())(
        releaseGuid
      );
      const audioCount = getReleaseSongOrVersionCount(getState());
      const handler = releaseType && handleReleaseExportsByType[releaseType];
      if (handler) {
        handler(
          { ...wsPayload, isAudioUploadSupported },
          releaseGuid,
          dispatch,
          audioCount
        );
      }
      break;
    }
    case 'ReleaseExportShipmentSuccess': {
      const wsPayload = jsonMessage.payload as ReleaseExportStatusUpdatePayload;
      const { parentGuid, status } = wsPayload;

      const updateStatus = async () => {
        const { latestExportShipment } = await dispatch(
          fetchReleaseEffect({ guid: parentGuid || '', silent: true })
        ).unwrap();
        dispatch(
          updateLatestExportAction({
            export: {
              ...latestExportShipment,
              parentGuid,
            } as BaseRelease['latestExportShipment'],
            date: dateToTime(new Date()),
          })
        );
      };
      if (status === 'SUCCESS') {
        updateStatus();
      }
      break;
    }
    case 'PdfCreated':
    case 'DonwloadableArchiveCreated':
    case 'DownloadableArchiveCreated': {
      const wsPayload = jsonMessage.payload as DownloadableResourcePayload;
      const downloadList = getDownloadList(getState());
      const href = wsPayload?.downloadUrl?.href;
      const id = wsPayload?.resourceGuid;
      if (!href || !id) {
        return;
      }

      const isDownloadInitiated = Boolean(
        downloadList.find((guid) => guid === id)
      );

      if (isDownloadInitiated) {
        dispatch(removeFromDownloadList(id));
        triggerDownload(href, id);
        dispatch(
          addAlertAction({
            id,
            message: 'Download completed',
            statusIcon: AlertIcon.SUCCESS,
            autoHideDuration: SNACKBAR_DEFAULT_CLOSE_TIME,
          })
        );
      }
      break;
    }
    case 'ReleaseVersionAudioUpdate': {
      const wsPayload = jsonMessage.payload as ReleaseVersionAudioUpdatePayload;
      const { originalAudioGuid, primaryAudioGuid, versionGuid } = wsPayload;
      if (originalAudioGuid && primaryAudioGuid && versionGuid) {
        dispatch(
          handleReplaceAudioUpdate({
            originalAudioGuid,
            primaryAudioGuid,
            versionGuid,
          })
        );
      }
      break;
    }
    case 'ReleaseIsReadyToExportEvent': {
      const wsPayload = jsonMessage.payload as ReleaseIsReadyToExportEventPayload;
      const { releaseGuid } = wsPayload;
      dispatch(fetchReleaseEffect({ guid: releaseGuid }));
      dispatch(
        addAlertAction({
          id: releaseGuid,
          message: 'Release processing is completed',
          statusIcon: AlertIcon.SUCCESS,
          autoHideDuration: SNACKBAR_DEFAULT_CLOSE_TIME,
        })
      );
      break;
    }
    case 'AudioCreated': {
      const wsPayload = jsonMessage.payload as AudioCreatedPayload;
      const state = getState();
      const songGuid = getSongGuidSelector(state);
      const currentUserGuid = getUserGuid(state);
      const isFetchingAudio = getIsFetchingAudio(state);
      const isSongAudioTab = isPageView(routes.songAudioList);

      const { audioGuid, parentGuid, createdBy, audioTitle } = wsPayload;

      if (audioGuid && parentGuid) {
        dispatch(setAudioUploadedAction({ audioTitle, status: 'uploaded' }));
      }

      if (
        audioGuid &&
        parentGuid === songGuid &&
        isSongAudioTab &&
        !isFetchingAudio[audioGuid]
      ) {
        dispatch(fetchSongAudioEffect({ guid: audioGuid }));
        if (currentUserGuid === createdBy) {
          dispatch(
            addAlertAction({
              message: `Audio file "${audioTitle}" has been uploaded`,
            })
          );
        }
      }
      break;
    }
    case 'AudioUploadProgress': {
      const wsPayload = jsonMessage.payload as AudioUploadProgressPayload;
      const { audioGuid, status, statusReason, parentGuid } = wsPayload;
      const state = getState();
      const songGuid = getSongGuidSelector(state);
      const audio = getSongAudioByGuid(state, audioGuid);
      const isSongAudioTab = isPageView(routes.songAudioList);
      if (isSongAudioTab && parentGuid === songGuid) {
        dispatch(
          updateSongAudioStatus({ status, statusReason, guid: audioGuid })
        );
        if (status === AudioStatusType.FAILURE && audio) {
          dispatch(
            addAlertAction({
              message: getSnackbarErrorText({ ...audio, status, statusReason }),
            })
          );
        }
      }
      break;
    }
    case 'AudioUpdated':
    case 'AudioDeleted':
    case 'LatestVersionAudioMarked':
    case 'LatestVersionAudioDeleted': {
      const wsPayload = jsonMessage.payload as AudioUpdatedPayload;
      dispatch(updateSongAudioAction(wsPayload));
      break;
    }
    case 'UserTotalStorageChanged': {
      const wsPayload = jsonMessage.payload as StorageChangedPayload;
      dispatch(updateStorageData(wsPayload));
      break;
    }

    case 'ChatChannelMessageEdited': {
      const item = jsonMessage.payload as EditChatMessageItem;
      const editedMessage = {
        ...item,
        created: isoToTime(item.created),
        updated: isoToTime(item.updated),
        ...(item.deleted ? { deleted: isoToTime(item?.deleted) } : {}),
      };
      dispatch(messageEditedAction(editedMessage));
      break;
    }

    case 'ChatChannelMessageSent': {
      const item = jsonMessage.payload as ChatMessageItem;
      const message: ChatMessageItem = {
        ...item,
        created: isoToTime(item.created),
        updated: isoToTime(item.updated),
      };

      const isChannelsListOpen = getIsChannelsListOpen(getState());

      dispatch(
        messageReceivedAction({
          message,
          shouldUpdateChannels: isChannelsListOpen,
        })
      );
      break;
    }

    case 'ChatChannelUpdate': {
      dispatch(
        fetchSongChannelEffect({
          ...jsonMessage.payload,
          update: true,
        } as FetchChannelEffectData)
      );
      break;
    }

    case 'ChannelUserUnreadStateChanged': {
      const wsPayload = jsonMessage.payload as ChatMessageUnreadCountChangedPayload;
      const channels = getChatChannels(getState());
      const insNewChannel = !channels.find(
        (channel) => channel.guid === wsPayload.channelGuid
      );
      dispatch(updateHasUnreadMessages(wsPayload));

      if (insNewChannel) {
        dispatch(fetchChatChannelsEffect());
      }

      if (!wsPayload.unreadMessageCount) {
        dispatch(fetchChatUnreadCountEffect());
      }

      break;
    }

    case 'UnreadNotificationCountChanged': {
      const wsPayload = jsonMessage.payload as UnreadNotificationCountChangedPayload;
      dispatch(updateNewNotificationsCountsAction(wsPayload));
      break;
    }

    case 'CsvImportProgress': {
      const wsPayload = jsonMessage.payload as CsvImportProgressPayload;
      dispatch(updateImportProgressAction(wsPayload));
      break;
    }

    case 'QuotaRateLimitReached':
    case 'QuotaRateLimitReset': {
      const {
        userGuid,
        resetAt,
      } = jsonMessage.payload as LyricsCowriterPayload;

      const currentUserGuid = getUserGuid(getState());
      if (userGuid === currentUserGuid) {
        const reset = jsonMessage.payload.payloadType === 'QuotaRateLimitReset';
        dispatch(setLyricsCowriterRequestRateLimit({ iso: resetAt, reset }));
      }
      break;
    }

    default:
      // eslint-disable-next-line no-console
      console.log(
        `Unknown message payload type: ${jsonMessage.payload.payloadType}`
      );
      break;
  }
};
